import { defineStore, acceptHMRUpdate } from 'pinia';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import {
    loadScimConfiguration,
    createScimConfiguration,
    createScimConfigurationCustomValidation,
    updateScimConfigurationCustomValidation,
    regenerateAPIKey,
    removeScimConfiguration,
    updateIdPGroupPermissionsForSub,
} from '@/vuex/modules/organization/org-admin.utils';

interface Organization {
    id?: string;
    organizationFlags?: Record<string, boolean>;
    [key: string]: unknown;
}

interface ScimConfiguration {
    id?: string;
    [key: string]: unknown;
}

interface OrganizationState {
    active: Organization;
    scimConfiguration: ScimConfiguration;
    error: unknown;
}

export function getInitialState (): OrganizationState {
    return {
        active: {},
        scimConfiguration: {},
        error: null,
    };
}

export const useOrganizationStore = defineStore('organization', {
    state: getInitialState,
    getters: {
        getActive: (state) => state.active,
        getFlag: (state) => (flag: string) => !!get(state.active, `organizationFlags.${flag}`, false),
    },
    actions: {
        setActive (activeOrganization: Organization) {
            if (!isObject(activeOrganization)) return;
            this.active = activeOrganization;
        },
        setScimConfiguration (scimConfiguration: ScimConfiguration) {
            if (!isObject(scimConfiguration)) return;
            this.scimConfiguration = scimConfiguration;
        },
        setError (error: unknown) {
            this.error = error;
        },
        async loadScimConfiguration ({ organizationId, reFetch = false }: { organizationId?: string; reFetch?: boolean } = {}) {
            if (!reFetch) return;
            const orgId = organizationId || this.getActive.id;
            try {
                const scimConfiguration = await loadScimConfiguration(orgId);
                this.setScimConfiguration(scimConfiguration);
            } catch (error) {
                this.setError(error);
            }
        },
        async createScimConfiguration ({ organizationId, reFetch = false }: { organizationId?: string; reFetch?: boolean } = {}) {
            if (!reFetch) return;
            const orgId = organizationId || this.getActive.id;
            try {
                const scimConfiguration = await createScimConfiguration(orgId);
                this.setScimConfiguration(scimConfiguration);
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async createScimConfigurationCustomValidation ({
            organizationId,
            issuer,
            audience,
            jwksUri,
            reFetch = false,
        }: { organizationId?: string; issuer?: string; audience?: string; jwksUri?: string; reFetch?: boolean } = {}) {
            if (!reFetch) return;
            const orgId = organizationId || this.getActive.id;
            try {
                const scimConfiguration = await createScimConfigurationCustomValidation(orgId, issuer, audience, jwksUri);
                this.setScimConfiguration(scimConfiguration);
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async updateScimConfigurationCustomValidation ({
            organizationId,
            configurationId,
            issuer,
            audience,
            jwksUri,
            reFetch = false,
        }: { organizationId?: string; configurationId?: string; issuer?: string; audience?: string; jwksUri?: string; reFetch?: boolean } = {}) {
            if (!reFetch) return;
            const orgId = organizationId || this.getActive.id;
            const configId = configurationId || this.scimConfiguration?.id;
            try {
                const scimConfiguration = await updateScimConfigurationCustomValidation(orgId, configId, issuer, audience, jwksUri);
                this.setScimConfiguration(scimConfiguration);
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async regenerateAPIKey ({ organizationId, configurationId, reFetch = false }: { organizationId?: string; configurationId?: string; reFetch?: boolean } = {}) {
            if (!reFetch) return;
            const orgId = organizationId || this.getActive.id;
            const configId = configurationId || this.scimConfiguration?.id;
            try {
                const scimConfiguration = await regenerateAPIKey(orgId, configId);
                this.setScimConfiguration(scimConfiguration);
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async removeScimConfiguration ({ organizationId, configurationId }: { organizationId?: string; configurationId?: string } = {}) {
            const orgId = organizationId || this.getActive.id;
            const configId = configurationId || this.scimConfiguration?.id;
            try {
                await removeScimConfiguration(orgId, configId);
                this.setScimConfiguration({});
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
        async updateIdPGroupPermissionsForSub ({
            organizationId,
            configurationId,
            subscriptionId,
            payload,
        }: { organizationId?: string; configurationId?: string; subscriptionId?: string; payload?: unknown } = {}) {
            const orgId = organizationId || this.getActive.id;
            const configId = configurationId || this.scimConfiguration?.id;
            try {
                const scimConfiguration = await updateIdPGroupPermissionsForSub(orgId, configId, subscriptionId, payload);
                this.setScimConfiguration(scimConfiguration);
            } catch (error) {
                this.setError(error);
                throw error;
            }
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useOrganizationStore, module.hot));
}
